import { Bell, CalendarClock, MapPin, Users } from "lucide-react";
import type { CSSProperties, FC, ReactNode } from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import {
  EVENT_COLOR_CLASSES,
  eventTone,
  formatEventTime,
  formatTimeShort,
  type CalendarEvent,
  type EventTone
} from "../calendarModel";
import { reminderFocusDomId } from "../remindersModel";

const TONE_CLASSES: Record<EventTone, string> = {
  default: "",
  done: "opacity-60 line-through decoration-current/60",
  overdue: "ring-1 ring-destructive/50 ring-inset",
  external: ""
};

const SOURCE_LABEL: Record<CalendarEvent["source"], string> = {
  reminder: "",
  google: "Google Calendar",
  outlook: "Outlook"
};

/** A reminder's kind; synced events carry no glyph. */
const KindGlyph: FC<{ event: CalendarEvent; className?: string }> = ({
  event,
  className
}) => {
  if (event.source !== "reminder") return null;
  const Icon = event.kind === "APPOINTMENT" ? CalendarClock : Bell;
  return <Icon className={cn("size-3 shrink-0 opacity-70", className)} />;
};

/** A people icon and count on an event merged from several teammates' calendars. */
const SharedCount: FC<{ event: CalendarEvent; className?: string }> = ({
  event,
  className
}) => {
  const n = event.ownerUids?.length ?? 0;
  if (n < 2) return null;
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-0.5 text-xs text-fg-3",
        className
      )}
      aria-label={`On ${n} calendars`}
      data-testid="event-shared-count"
    >
      <Users className="size-2.5" />
      {n}
    </span>
  );
};

/** "Title · Client" when the event is shown outside its client's profile. */
const EventTitle: FC<{ event: CalendarEvent; className?: string }> = ({
  event,
  className
}) => (
  <span className={cn("min-w-0 truncate", className)}>
    {event.title}
    {event.clientName ? (
      <span className="font-normal opacity-70"> · {event.clientName}</span>
    ) : null}
  </span>
);

/** Tooltip body shared by the pill and the block. */
const EventTooltip: FC<{ event: CalendarEvent }> = ({ event }) => (
  <div className="max-w-64 space-y-0.5">
    <p className="font-medium">{event.title}</p>
    <p className="text-muted-foreground">{formatEventTime(event)}</p>
    {event.clientName ? (
      <p className="text-muted-foreground">{event.clientName}</p>
    ) : null}
    {event.source !== "reminder" ? (
      <p className="text-muted-foreground">
        {(event.ownerUids?.length ?? 0) > 1
          ? `On ${event.ownerUids?.length} calendars`
          : SOURCE_LABEL[event.source]}
      </p>
    ) : null}
    {event.location ? (
      <p className="flex items-center gap-1 text-muted-foreground">
        <MapPin className="size-3" /> {event.location}
      </p>
    ) : null}
    {event.source === "reminder" && event.status !== "OPEN" ? (
      <p className="text-muted-foreground">
        {event.status === "COMPLETED" ? "Completed" : "Dismissed"}
      </p>
    ) : null}
  </div>
);

type ChipProps = {
  event: CalendarEvent;
  highlighted?: boolean;
  onOpen: (event: CalendarEvent) => void;
  now?: Date;
};

/**
 * Compact one-line event for month cells, all-day rows and popovers:
 * "10am Team Meeting" or just the title for all-day entries.
 */
export const EventPill: FC<
  ChipProps & {
    /** Hide the time prefix (all-day rows / spans). */
    hideTime?: boolean;
    className?: string;
    style?: CSSProperties;
    /** Extra leading/trailing decoration (e.g. clipped-span arrows). */
    leading?: ReactNode;
    trailing?: ReactNode;
  }
> = ({
  event,
  highlighted = false,
  onOpen,
  now,
  hideTime = false,
  className,
  style,
  leading,
  trailing
}) => {
  const tone = eventTone(event, now);
  const showTime = !hideTime && !event.allDay;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          id={reminderFocusDomId(event.id)}
          data-focus-id={reminderFocusDomId(event.id)}
          data-event-id={event.id}
          onClick={(e) => {
            e.stopPropagation();
            onOpen(event);
          }}
          style={style}
          className={cn(
            "flex h-5.5 w-full min-w-0 items-center gap-1 rounded-sm px-1.5 text-left text-xs leading-none transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
            EVENT_COLOR_CLASSES[event.color].pill,
            TONE_CLASSES[tone],
            highlighted && "ring-1 ring-primary ring-offset-1",
            className
          )}
        >
          {leading}
          {showTime ? (
            <span className="shrink-0 opacity-70">
              {formatTimeShort(event.start)}
            </span>
          ) : (
            <KindGlyph event={event} />
          )}
          <EventTitle event={event} className="font-medium" />
          <SharedCount event={event} className="ml-auto" />
          {trailing}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" align="start">
        <EventTooltip event={event} />
      </TooltipContent>
    </Tooltip>
  );
};

/** Absolutely positioned block for the week/day time grid. */
export const EventBlock: FC<
  ChipProps & { style: CSSProperties; compact?: boolean }
> = ({ event, highlighted = false, onOpen, now, style, compact = false }) => {
  const tone = eventTone(event, now);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          id={reminderFocusDomId(event.id)}
          data-focus-id={reminderFocusDomId(event.id)}
          data-event-id={event.id}
          onClick={(e) => {
            e.stopPropagation();
            onOpen(event);
          }}
          style={style}
          className={cn(
            "absolute flex min-w-0 overflow-hidden rounded-xs px-1.5 text-left text-sm transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
            compact ? "items-center gap-1" : "flex-col py-1",
            EVENT_COLOR_CLASSES[event.color].pill,
            TONE_CLASSES[tone],
            highlighted && "ring-1 ring-primary"
          )}
        >
          <span
            className={cn(
              "flex min-w-0 items-center gap-1",
              compact && "flex-1"
            )}
          >
            <KindGlyph event={event} />
            <EventTitle event={event} />
            {compact ? <SharedCount event={event} className="ml-auto" /> : null}
          </span>
          {!compact ? (
            <span className="truncate text-xs opacity-70">
              {formatEventTime(event)}
            </span>
          ) : null}
          {!compact ? <SharedCount event={event} className="mt-0.5" /> : null}
        </button>
      </TooltipTrigger>
      <TooltipContent side="right" align="start">
        <EventTooltip event={event} />
      </TooltipContent>
    </Tooltip>
  );
};

/** Colored dot used by the agenda and the "+N more" list. */
export const EventDot: FC<{ event: CalendarEvent; className?: string }> = ({
  event,
  className
}) => (
  <span
    aria-hidden
    className={cn(
      "inline-block size-2 shrink-0 rounded-full",
      EVENT_COLOR_CLASSES[event.color].dot,
      className
    )}
  />
);
